import { yupResolver } from '@hookform/resolvers/yup';
import SearchIcon from '@mui/icons-material/Search';
import { CardHeader, Grid } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useStoreSelector } from 'v3/data/store';
import { ContractingStateEnum } from 'v3/data/store/contracting/enums';
import { filtersInitialState } from 'v3/data/store/contracting/state';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { clearFilters, setContractingList, setFilters } from 'v3/data/store/payment/actions';
import { restoreStoreList } from 'v3/data/store/stores/actions';
import { IStoreItem } from 'v3/data/store/stores/types';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import * as api from 'v3/services/api';
import { MiniButton } from 'v3/shared/components/Button/MiniButton';
import { filterRequestFactory } from 'v3/shared/components/Filters/MainFilters/factory';
import { PaymentsFilterType } from 'v3/shared/components/Filters/PaymentsFilter/types';
import { InputFormController } from 'v3/shared/components/Inputs/InputFormController';
import { TextMaskCustom } from 'v3/shared/components/Inputs/MaskCustomInput';
import { paymentFilterSchema } from 'v3/shared/schemas/paymentFilter.schema';
import { BodyLarge } from 'v3/shared/typography/BodyLarge';
import { theme } from 'v3/styles/global-styles';
import { FlexButton } from '../../Button/FlexButton';
import { AlignButtons, CardContentStyled, CardStyled } from './styles';

const FIXED_SEARCH_STATUS = ContractingStateEnum.ContractingFinished;

export const PaymentsFilter = () => {
  const { isTablet, isMobile } = useBreakpoints();
  const filterState = useStoreSelector((store) => store.payment.filter);
  const storeList = useStoreSelector((store) => store.store.storeList);
  const { id: loggedStoreId } = useStoreSelector((store) => store.store.storeLogged);

  const {
    control,
    getValues,
    reset,
    formState: { isValid },
  } = useForm<PaymentsFilterType>({
    mode: 'onChange',
    defaultValues: {
      ...filterState,
      status: FIXED_SEARCH_STATUS,
    },
    reValidateMode: 'onChange',
    resolver: yupResolver(paymentFilterSchema) as any,
  });

  const filterOnlyCheckedStores = useCallback((stores: IStoreItem[]) => {
    return stores.filter((store) => store.checked).map((store) => store.id);
  }, []);

  const getContractingList = useCallback(async () => {
    const desiredStoreIds = filterOnlyCheckedStores(storeList);

    const finalFiltersForApi = {
      ...filterRequestFactory(filterState, filtersInitialState),
      status: FIXED_SEARCH_STATUS,
      searchString: filterState.searchString,
    };

    try {
      const response = await api.getContractingList({
        ...finalFiltersForApi,
        storeIds: desiredStoreIds,
        storeId: loggedStoreId,
      });
      setContractingList(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista de contratações', error);
    }
  }, [filterState, storeList, loggedStoreId, filterOnlyCheckedStores]);

  useEffect(() => {
    getContractingList();
  }, []);

  const handleSearch = useCallback(() => {
    let searchStringFromForm = getValues('searchString');

    if (searchStringFromForm) {
      searchStringFromForm = searchStringFromForm.replace(/\D/g, '');
    }

    setFilters({
      ...filterState,
      searchString: searchStringFromForm,
      isCustomDate: false,
      period: 'month',
    });
  }, [getValues, filterState]);

  const handleClear = useCallback(() => {
    clearFilters();
    restoreStoreList(storeList);
    reset({
      ...filtersInitialState,
      searchString: '',
      status: FIXED_SEARCH_STATUS,
    });
  }, [reset, storeList]);

  return (
    <CardStyled>
      <CardHeader title={<BodyLarge>Filtro</BodyLarge>} />
      <CardContentStyled>
        <Grid
          container
          spacing={0}
          sx={{
            [theme.breakpoints.down('sm')]: {
              marginBottom: theme.spacing(2),
            },
          }}
        >
          <Grid size={{ xs: 12, sm: 8, md: 6, lg: 7 }} component="div">
            <InputFormController
              data-testid="input-cpf"
              inputType="tel"
              formControl={control}
              formControlName="searchString"
              placeholder="Procurar por CPF"
              customMask={TextMaskCustom}
              mask="000.000.000-00"
              fullWidth
              startAdornmentIcon={SearchIcon}
              showCheckbox={false}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 4, md: 6, lg: 5 }} order={{ xs: 99, sm: 'unset' }} component="div">
            <AlignButtons>
              <MiniButton text="Limpar" $backgroundColor="transparent" handleClick={handleClear} />
              {isTablet || isMobile ? (
                <MiniButton
                  icon={isTablet ? SearchIcon : undefined}
                  text={isMobile ? 'Pesquisar' : undefined}
                  handleClick={handleSearch}
                />
              ) : (
                <FlexButton text="Pesquisar" size="small" disabled={!isValid} handleClick={handleSearch} />
              )}
            </AlignButtons>
          </Grid>
        </Grid>
      </CardContentStyled>
    </CardStyled>
  );
};
